import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, Play, RotateCcw, Zap, Users, Trophy } from "lucide-react";

export default function Diagnostics() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sport, setSport] = useState<'soccer' | 'csgo'>('soccer');
  const [teamCount, setTeamCount] = useState('8');
  const [currentLeague, setCurrentLeague] = useState<any>(null);
  const [draftState, setDraftState] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user?.email === 'remy@draftactics.com') {
      setIsAdmin(true);
    }
  }, [user]);

  const callDraftAdmin = async (action: string, params = {}) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('draft-admin', {
        body: { action, ...params }
      });
      
      if (error) throw error;
      toast({ title: "Success", description: data.message });
      return data;
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Authentication Required</h1>
          <p>Please log in to access the diagnostics panel.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p>This diagnostics panel is only available to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Draft Diagnostics</h1>
        <p className="text-muted-foreground">Owner-only admin panel for testing draft functionality</p>
        <Badge variant="secondary" className="mt-2">Admin Access - {user.email}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Sport</Label>
              <RadioGroup value={sport} onValueChange={(value: 'soccer' | 'csgo') => setSport(value)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="soccer" id="soccer" />
                  <Label htmlFor="soccer">Soccer</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="csgo" id="csgo" />
                  <Label htmlFor="csgo">CS:GO</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div>
              <Label>Team Count</Label>
              <Select value={teamCount} onValueChange={setTeamCount}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[4,6,8,10,12].map(n => <SelectItem key={n} value={n.toString()}>{n} Teams</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Button onClick={() => callDraftAdmin('createSandboxLeague', { sport, teams: parseInt(teamCount) })} disabled={loading} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} Create Sandbox League
              </Button>
              <Button onClick={() => callDraftAdmin('createBotProfiles')} disabled={loading} variant="outline" className="w-full">
                Create Bot Profiles
              </Button>
              <Button onClick={() => callDraftAdmin('seedPlayerRankings', { sport })} disabled={loading} variant="outline" className="w-full">
                Seed Player Rankings
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />Draft Controls
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button onClick={() => callDraftAdmin('generateDraftOrder', { leagueId: currentLeague?.id })} disabled={loading || !currentLeague} className="w-full">
              Generate Draft Order
            </Button>
            <Button onClick={() => callDraftAdmin('startDraft', { leagueId: currentLeague?.id })} disabled={loading} className="w-full">
              <Play className="h-4 w-4" />Start Draft
            </Button>
            <Button onClick={() => callDraftAdmin('autoPick', { leagueId: currentLeague?.id })} disabled={loading} variant="outline" className="w-full">
              <Zap className="h-4 w-4" />Auto-Pick (once)
            </Button>
            <Button onClick={() => callDraftAdmin('runFullBotDraft', { leagueId: currentLeague?.id })} disabled={loading} className="w-full">
              Run Full Bot Draft
            </Button>
            <Button onClick={() => callDraftAdmin('resetSandbox', { leagueId: currentLeague?.id })} disabled={loading} variant="destructive" className="w-full">
              <RotateCcw className="h-4 w-4" />Reset Sandbox
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}